/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Modal, Button } from 'antd';
import { FileAndLinkUploadMenu, FilePreviewModal, extractFileNameFromUrl } from '@/components/common/FileAndLinkUpload';
import CommonJs from '@/utils/commonJs';
import { DeleteOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';

interface UploadModalProps {
  visible: boolean;
  selectedRecord: any | null;
  documentFiles: string[];
  onCancel: () => void;
  onSave: (files: string[]) => void;
  /** Ẩn nút Lưu và không cho thêm/xóa file khi không có quyền sửa. */
  readOnly?: boolean;
}

export const UploadModal: React.FC<UploadModalProps> = ({
  visible,
  selectedRecord,
  documentFiles,
  onCancel,
  onSave,
  readOnly = false,
}) => {
  const [localFiles, setLocalFiles] = React.useState<string[]>(documentFiles);
  const [previewFile, setPreviewFile] = React.useState<{ fileName: string; filePath: string } | null>(null);

  React.useEffect(() => {
    setLocalFiles(documentFiles);
  }, [documentFiles]);

  const appendFileUrls = React.useCallback((urls: string[]) => {
    const newUrls = urls.map((u) => String(u).trim()).filter(Boolean);
    if (newUrls.length === 0) return;

    setLocalFiles((prev) => {
      const merged = [...prev, ...newUrls].filter((u: string) => Boolean(String(u).trim()));
      const seen = new Set<string>();
      return merged.filter((u: string) => {
        if (seen.has(u)) return false;
        seen.add(u);
        return true;
      });
    });
  }, []);

  const handleRemove = (index: number) => {
    setLocalFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    onSave(localFiles);
  };

  const handleOpenPreview = (filePath: string) => {
    const fileName = extractFileNameFromUrl(filePath);
    if (!fileName) return;
    setPreviewFile({ fileName, filePath });
  };

  const taskTitle =
    selectedRecord?.congViec?.tenCongViec ||
    selectedRecord?.tenCongViec ||
    selectedRecord?.originalData?.congViec?.tenCongViec ||
    '';

  return (
    <>
      <Modal
        open={visible}
        title={`Tài liệu: ${taskTitle}`}
        onCancel={onCancel}
        width={600}
        centered
        footer={
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            {!readOnly ? (
              <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', justifyContent: 'flex-start' }}>
                <FileAndLinkUploadMenu
                  block={false}
                  urlModalZIndex={1200}
                  onAppendUrls={(urls) => {
                    appendFileUrls(urls);
                  }}
                />
              </div>
            ) : (
              <span />
            )}
            <div style={{ display: 'flex', gap: 10, flexShrink: 0, marginLeft: 'auto' }}>
              {!readOnly && (
                <Button type="primary" onClick={handleSave}>
                  Lưu
                </Button>
              )}
              <Button type="default" onClick={onCancel}>
                Đóng
              </Button>
            </div>
          </div>
        }
      >
        <div>
          {!Array.isArray(localFiles) || localFiles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>Chưa có tài liệu nào</div>
          ) : (
            <div>
              {localFiles.map((file, index) => {
                const fileName = file.split('/').pop() || file;
                return (
                  <div
                    key={index}
                    style={{
                      padding: '10px 12px',
                      marginBottom: '8px',
                      background: '#f8fafc',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 13,
                        color: '#111827',
                      }}
                      title={fileName}
                    >
                      {fileName}
                    </span>
                    <div
                      style={{
                        display: 'flex',
                        gap: 4,
                        marginLeft: '10px',
                        padding: '2px',
                        borderRadius: 8,
                        background: '#ffffff',
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={<EyeOutlined />}
                        title="Xem trước"
                        onClick={() => handleOpenPreview(file)}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                      />
                      <Button
                        type="text"
                        size="small"
                        icon={<DownloadOutlined />}
                        title="Tải xuống"
                        onClick={() => CommonJs.downloadFile(file, fileName)}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                      />
                      {!readOnly && (
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          title="Xóa"
                          onClick={() => handleRemove(index)}
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal>
      <FilePreviewModal
        open={!!previewFile}
        filePath={previewFile?.filePath || ''}
        fileName={previewFile?.fileName}
        onClose={() => setPreviewFile(null)}
        zIndex={1300}
      />
    </>
  );
};

